/*
 * This file is part of the Web3 Library developed by mFactory GmbH.
 *
 * Copyright © 2021, mFactory GmbH
 *
 * Solana Reference Stake Pool is free software: you can redistribute it
 * and/or modify it under the terms of the GNU Affero General Public License
 * as published by the Free Software Foundation, either version 3
 * of the License, or (at your option) any later version.
 *
 * Solana Reference Stake Pool is distributed in the hope that it
 * will be useful, but WITHOUT ANY WARRANTY; without even the implied
 * warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.
 * If not, see <https://www.gnu.org/licenses/agpl-3.0.html>.
 *
 * You can be released from the requirements of the Affero GNU General Public License
 * by purchasing a commercial license. The purchase of such a license is
 * mandatory as soon as you develop commercial activities using the
 * Solana Reference Stake Pool code without disclosing the source code of
 * your own applications.
 *
 * The developer of this program can be contacted at <info@mfactory.ch>.
 */

import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { EPOCH_RELOAD_INTERVAL } from '~/config'
import { useEmitter } from '~/hooks'
import { EpochInfo, getEpochInfo } from '~/utils'

// eslint-disable-next-line symbol-description
export const EPOCH_UPDATE_EVENT = Symbol()

export const useEpochStore = defineStore('epoch', () => {
  const emitter = useEmitter()

  const epochInfo = ref<EpochInfo>()
  const epochTimeRemaining = ref(0)
  const epochNumber = ref(0)
  const epochTimeTotal = ref(0)

  const validatorActiveStore = useValidatorActiveStore()
  const network = computed(() => validatorActiveStore.network)

  const epochProgress = computed(() => {
    if (!epochInfo.value) {
      return 100
    }
    return ((100 * epochInfo.value.slotIndex) / epochInfo.value.slotsInEpoch).toFixed(1)
  })

  // Try to load epoch info
  const loadEpochInfo = () => getEpochInfo(network.value).then(e => (epochInfo.value = e))

  watch(
    network,
    () => {
      loadEpochInfo()
    },
    { immediate: true },
  )

  watch(epochInfo, (e) => {
    epochTimeRemaining.value = e ? (e.slotsInEpoch - e.slotIndex) * e.hourlySlotTime : 0
    epochTimeTotal.value = e ? e.slotsInEpoch * e.hourlySlotTime : 0
    if (e && e.epoch !== epochNumber.value) {
      epochNumber.value = e.epoch
    }
  })

  // Re-sync epoch info
  setInterval(async () => {
    await loadEpochInfo()
    emitter.emit(EPOCH_UPDATE_EVENT, epochInfo.value)
    console.log('Reload epoch info', epochInfo.value)
  }, EPOCH_RELOAD_INTERVAL)

  // Update timer
  setInterval(async () => {
    const newVal = epochTimeRemaining.value - 1000
    epochTimeRemaining.value = Math.max(newVal, 0)
    // console.log('epochTimeRemaining', epochTimeRemaining.value);
    // console.log('epochProgress', epochProgress.value);
  }, 1000)

  return {
    epochInfo,
    epochNumber,
    epochTimeRemaining,
    epochProgress,
    epochTimeTotal,
    reload: () => loadEpochInfo(),
  }
})
